import { useEffect } from "react";
import { useThree } from "@react-three/fiber";
import { useSnapshot } from "valtio";
import * as THREE from "three";
import { stateNavigation, stateContentReady, stateMemory, stateLoadingScreen } from "../store";

// Three.js lädt Texturen erst in den GPU-Speicher (und kompiliert Shader),
// wenn ein Mesh ZUM ERSTEN MAL im Kamerasichtfeld auftaucht — das passiert
// sonst mitten in der Kamerafahrt und verursacht das Erstflug-Ruckeln an
// Schaustellen (am stärksten Salzentstehung, viele große Texturen).
// Dieser Helfer zieht beide Arbeiten auf Momente vor, in denen die Kamera
// still steht: Shader via compileAsync (parallel, ohne Jank), Textur-Uploads
// via initTexture.
// Sichtbarkeits-Regel: Auf speicherschwachen Geräten (memoryLowDevice) nur
// SICHTBARE Objekte — die Speicher-Strategie (Texturen unsichtbarer Räume
// bleiben unten) bleibt dort erhalten. Auf allen anderen Geräten auch
// unsichtbare Teilbäume: Ziel-Schaustellen werden beim Klick sofort visible
// geflippt (noch VOR der Kamerafahrt), ihr Erst-Upload landete sonst also
// doch wieder mitten im Flug (z.B. Mittelalter → Moderner Bergbau).
// initTexture fasst ohnehin nur Texturen an, die bereits im RAM liegen.
export default function GpuPrewarm() {
  const { gl, scene, camera } = useThree();
  const snapNav = useSnapshot(stateNavigation);
  const snapContentReady = useSnapshot(stateContentReady);
  const snapLoading = useSnapshot(stateLoadingScreen);

  useEffect(() => {
    // Nur prewarmen, wenn die Kamera steht — nie während einer Fahrt.
    if (snapNav.cameraAnimationRuns) return;
    // Und erst, wenn der Startscreen KOMPLETT weggefadet ist: framer-motion
    // animiert dessen Opacity per JS — ein Prewarm-Blocker mitten im Fade
    // friert die Ausblendung sichtbar ein ("blinkt nochmal auf").
    if (!snapLoading.exitComplete) return;

    let cancelled = false;

    // Kurz warten, bis Ankunfts-UI/Streaming des Raums durch ist.
    const timer = setTimeout(() => {
      if (cancelled) return;

      const compiled = gl.compileAsync
        ? gl.compileAsync(scene, camera)
        : Promise.resolve(gl.compile(scene, camera));

      compiled
        .then(() => {
          if (cancelled) return;
          // Manuelle Rekursion statt scene.traverse — nur auf Low-Memory-
          // Geräten stoppen unsichtbare Teilbäume die Rekursion (s. oben).
          const skipInvisible = stateMemory.memoryLowDevice;
          const walk = (obj) => {
            if (!obj.visible && skipInvisible) return;
            const mats = Array.isArray(obj.material)
              ? obj.material
              : obj.material
                ? [obj.material]
                : [];
            for (const mat of mats) {
              for (const key in mat) {
                const val = mat[key];
                if (val && val.isTexture) gl.initTexture(val);
              }
            }
            for (const child of obj.children) walk(child);
          };
          walk(scene);

          if (cancelled) return;
          // Geometrie-Buffer (VBOs) gehen erst beim ersten echten Draw-Call
          // auf die GPU — compileAsync/initTexture decken das nicht ab. Ein
          // einmaliges Cube-Rendering (winziges 128er-Target, gleich wieder
          // entsorgt) zeichnet die sichtbare Szene in alle sechs Richtungen
          // und zwingt alle Buffer hoch — derselbe Trick wie in dreis
          // <Preload>, nur ohne unsichtbare Räume aufzudecken. Kostet einen
          // schweren Frame IM STAND (bewusster Trade-off statt Ruckeln
          // während der Kamerafahrt).
          // troika-<Text>-Meshes vorm Cube-Render aus dem Render ausblenden.
          // Hinweis: Der harmlose troika-"redefinition"-Shader-Fehler kommt
          // NICHT von hier, sondern von compileAsync oben (das kompiliert per
          // scene.traverse ALLE Materialien, auch unsichtbare — visible=false
          // schützt nur vorm Rendern, nicht vorm Kompilieren). Der Fehler ist
          // bewusst akzeptiert: App läuft weiter, troika kompiliert beim echten
          // Rendern sauber neu, reines Konsolen-Rauschen. Das Ausblenden hier
          // spart nur den (ohnehin winzigen) Text-VBO-Prewarm.
          let cubeTarget = null;
          const hiddenTroika = [];
          try {
            cubeTarget = new THREE.WebGLCubeRenderTarget(128);
            const cubeCam = new THREE.CubeCamera(0.1, 100000, cubeTarget);
            cubeCam.position.copy(camera.position);

            scene.traverse((obj) => {
              const m = obj.material;
              const isTroika = Array.isArray(m)
                ? m.some((x) => x && x.isTroikaTextMaterial)
                : m && m.isTroikaTextMaterial;
              if (isTroika && obj.visible) {
                obj.visible = false;
                hiddenTroika.push(obj);
              }
            });

            cubeCam.update(gl, scene);
          } catch (e) {
            /* Prewarm ist nur Optimierung — nie die App deswegen crashen */
          } finally {
            // Immer wiederherstellen/aufräumen — auch wenn oben etwas wirft,
            // sonst blieben die Labels unsichtbar bzw. das Target würde leaken.
            hiddenTroika.forEach((obj) => { obj.visible = true; });
            if (cubeTarget) cubeTarget.dispose();
          }
        })
        .catch(() => {});
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Neu anstoßen bei jeder Ankunft (cameraAnimationRuns → false), wenn
    // ein Raum neue Inhalte fertig gestreamt hat (stateContentReady) und
    // sobald der Startscreen weg ist (exitComplete → erster Lauf).
  }, [snapNav.cameraAnimationRuns, snapContentReady, snapLoading.exitComplete, gl, scene, camera]);

  return null;
}
